import { useState, useMemo } from "react";
import { ToolsShell, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Resistor Color Code Reader — bidirectional. Pick bands → get value, or
// enter value → get bands.
//
// Standard IEC 60062 color codes:
//   Band 1-3 (significant digits): black=0 through white=9
//   Multiplier: black=1 (×10⁰) through white=10⁹; gold=10⁻¹; silver=10⁻²
//   Tolerance: brown=1%, red=2%, green=0.5%, blue=0.25%, violet=0.1%,
//              grey=0.05%, gold=5%, silver=10%, (none)=20%
//
// 4-band: [digit1, digit2, multiplier, tolerance]
// 5-band: [digit1, digit2, digit3, multiplier, tolerance]
// (6-band adds a TCR coefficient. Excluded for simplicity; the 4 and 5
// band cover ~99% of consumer-electronics use.)

const COLORS = {
  black:  { hex: "#000000", digit: 0,  mult: 1e0,  tol: null,    name: "Black" },
  brown:  { hex: "#7B3F1B", digit: 1,  mult: 1e1,  tol: 1,       name: "Brown" },
  red:    { hex: "#C53030", digit: 2,  mult: 1e2,  tol: 2,       name: "Red" },
  orange: { hex: "#E07832", digit: 3,  mult: 1e3,  tol: null,    name: "Orange" },
  yellow: { hex: "#E5C13A", digit: 4,  mult: 1e4,  tol: null,    name: "Yellow" },
  green:  { hex: "#3A8C49", digit: 5,  mult: 1e5,  tol: 0.5,     name: "Green" },
  blue:   { hex: "#3066A1", digit: 6,  mult: 1e6,  tol: 0.25,    name: "Blue" },
  violet: { hex: "#7A4DAD", digit: 7,  mult: 1e7,  tol: 0.1,     name: "Violet" },
  grey:   { hex: "#8A8A8A", digit: 8,  mult: 1e8,  tol: 0.05,    name: "Grey" },
  white:  { hex: "#E8E8E8", digit: 9,  mult: 1e9,  tol: null,    name: "White" },
  gold:   { hex: "#C9A227", digit: null, mult: 1e-1, tol: 5,     name: "Gold" },
  silver: { hex: "#BFBFBF", digit: null, mult: 1e-2, tol: 10,    name: "Silver" },
};
const DIGIT_COLORS = ["black", "brown", "red", "orange", "yellow", "green", "blue", "violet", "grey", "white"];
const MULT_COLORS = ["black", "brown", "red", "orange", "yellow", "green", "blue", "violet", "grey", "white", "gold", "silver"];
const TOL_COLORS = ["brown", "red", "green", "blue", "violet", "grey", "gold", "silver"];

const formatOhms = (v) => {
  if (v >= 1e9) return `${(v / 1e9).toFixed(2)} GΩ`;
  if (v >= 1e6) return `${(v / 1e6).toFixed(2)} MΩ`;
  if (v >= 1e3) return `${(v / 1e3).toFixed(2)} kΩ`;
  if (v >= 1) return `${v.toFixed(2)} Ω`;
  return `${v.toFixed(3)} Ω`;
};

export default function ResistorColorCode() {
  const [mode, setMode] = useState("bands"); // bands | value
  const [bandCount, setBandCount] = useState(4);
  const [bands, setBands] = useState(["brown", "black", "red", "gold"]); // default = 1kΩ ±5%
  const [valueInput, setValueInput] = useState("1000");
  const [valueTolerance, setValueTolerance] = useState(5);

  const ensureBandCount = (n) => {
    setBandCount(n);
    setBands((b) => {
      if (n === 4) return [b[0] || "brown", b[1] || "black", b[b.length - 2] || "red", b[b.length - 1] || "gold"];
      // 5-band: insert a 3rd digit slot
      return [b[0] || "brown", b[1] || "black", "black", b[b.length - 2] || "red", b[b.length - 1] || "gold"];
    });
  };

  // ---- BANDS → VALUE ----
  const decoded = useMemo(() => {
    try {
      if (bandCount === 4) {
        const [d1, d2, m, t] = bands;
        const value = (COLORS[d1].digit * 10 + COLORS[d2].digit) * COLORS[m].mult;
        const tol = COLORS[t].tol;
        return { value, tol };
      } else {
        const [d1, d2, d3, m, t] = bands;
        const value = (COLORS[d1].digit * 100 + COLORS[d2].digit * 10 + COLORS[d3].digit) * COLORS[m].mult;
        const tol = COLORS[t].tol;
        return { value, tol };
      }
    } catch {
      return { err: "Pick a color for every band." };
    }
  }, [bandCount, bands]);

  // ---- VALUE → BANDS ----
  const encoded = useMemo(() => {
    const v = parseFloat(valueInput);
    if (isNaN(v) || v <= 0) return { err: "Enter a positive resistance value." };
    // Decompose into significand and exponent.
    // For 4-band: 2 significant digits → significand = round(v / 10^(exp-1))
    // For 5-band: 3 significant digits → significand = round(v / 10^(exp-2))
    const digits = bandCount === 4 ? 2 : 3;
    const exp = Math.floor(Math.log10(v));
    const sig = Math.round(v / Math.pow(10, exp - (digits - 1)));
    const multExp = exp - (digits - 1);
    if (multExp < -2 || multExp > 9) return { err: "Value out of representable range." };

    // Find the multiplier color
    const multColor = MULT_COLORS.find((c) => Math.abs(COLORS[c].mult - Math.pow(10, multExp)) < Math.pow(10, multExp) * 0.01);
    if (!multColor) return { err: "Couldn't find a matching multiplier color." };

    // Digits to colors
    const sigStr = String(sig).padStart(digits, "0");
    if (sigStr.length > digits) return { err: `Value needs more than ${digits} significant digits — try ${bandCount === 4 ? "5" : "4"}-band.` };
    const digitColors = sigStr.split("").map((d) => DIGIT_COLORS[parseInt(d, 10)]);

    // Tolerance to color
    const tolColor = TOL_COLORS.find((c) => COLORS[c].tol === valueTolerance) || "gold";

    return { bands: [...digitColors, multColor, tolColor] };
  }, [valueInput, bandCount, valueTolerance]);

  const setBand = (i, c) => {
    const next = [...bands];
    next[i] = c;
    setBands(next);
  };

  return (
    <ToolsShell title="Resistor Color Code">
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
        {[{ id: "bands", label: "Bands → Value" }, { id: "value", label: "Value → Bands" }].map((m) => (
          <button
            key={m.id}
            onClick={() => setMode(m.id)}
            style={{ background: mode === m.id ? TOOLS_ACCENT : "#1C1E2E", color: mode === m.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", flex: 1 }}
          >
            {m.label}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
        {[4, 5].map((n) => (
          <button
            key={n}
            onClick={() => ensureBandCount(n)}
            style={{ background: bandCount === n ? TOOLS_ACCENT : "#1C1E2E", color: bandCount === n ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            {n}-band
          </button>
        ))}
      </div>

      {mode === "bands" ? (
        <>
          <ResistorPreview bands={bands} bandCount={bandCount} />
          <div style={{ display: "grid", gridTemplateColumns: bandCount === 4 ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr 1fr 1fr", gap: 8, marginTop: 16 }}>
            {bands.map((c, i) => {
              const isMult = i === bandCount - 2;
              const isTol = i === bandCount - 1;
              const options = isTol ? TOL_COLORS : isMult ? MULT_COLORS : DIGIT_COLORS;
              const labels = ["1st", "2nd", "3rd", "Mult.", "Tol."];
              const labelIdx = bandCount === 4 ? (i === 2 ? 3 : i === 3 ? 4 : i) : i;
              return (
                <div key={i}>
                  <div style={{ fontSize: 11, color: "#7C8099", marginBottom: 4, textAlign: "center" }}>{labels[labelIdx]}</div>
                  <select
                    value={c}
                    onChange={(e) => setBand(i, e.target.value)}
                    style={{ width: "100%", background: COLORS[c].hex, color: ["white", "yellow", "silver"].includes(c) ? "#000" : "#fff", border: "1px solid #262839", borderRadius: 8, fontSize: 12, padding: "8px 4px", fontWeight: 600, textAlign: "center" }}
                  >
                    {options.map((o) => <option key={o} value={o} style={{ background: "#13141F", color: "#E8E9F3" }}>{COLORS[o].name}</option>)}
                  </select>
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 22, background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "18px 20px", textAlign: "center" }}>
            {decoded.err ? (
              <div style={{ color: "#F8716A" }}>{decoded.err}</div>
            ) : (
              <>
                <div style={{ fontSize: 36, fontWeight: 800, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>{formatOhms(decoded.value)}</div>
                <div style={{ fontSize: 14, color: "#9A9DBE", marginTop: 4 }}>±{decoded.tol}% tolerance</div>
              </>
            )}
          </div>
        </>
      ) : (
        <>
          <label style={{ fontSize: 13, color: "#9A9DBE", display: "block", marginBottom: 6 }}>Resistance (Ω)</label>
          <input
            value={valueInput}
            onChange={(e) => setValueInput(e.target.value)}
            inputMode="decimal"
            placeholder="e.g. 4700 for 4.7kΩ"
            style={{ width: "100%", background: "#13141F", border: "1px solid #262839", borderRadius: 10, color: "#E8E9F3", fontSize: 15, padding: "12px 14px", boxSizing: "border-box" }}
          />
          <label style={{ fontSize: 13, color: "#9A9DBE", display: "block", marginBottom: 6, marginTop: 14 }}>Tolerance</label>
          <select value={valueTolerance} onChange={(e) => setValueTolerance(parseFloat(e.target.value))} style={{ width: "100%", background: "#13141F", border: "1px solid #262839", borderRadius: 10, color: "#E8E9F3", fontSize: 15, padding: "12px 14px", boxSizing: "border-box" }}>
            <option value="0.1">0.1% (violet)</option>
            <option value="0.25">0.25% (blue)</option>
            <option value="0.5">0.5% (green)</option>
            <option value="1">1% (brown)</option>
            <option value="2">2% (red)</option>
            <option value="5">5% (gold)</option>
            <option value="10">10% (silver)</option>
          </select>

          {encoded.err ? (
            <div style={{ marginTop: 22, color: "#F8716A" }}>{encoded.err}</div>
          ) : (
            <div style={{ marginTop: 22 }}>
              <ResistorPreview bands={encoded.bands} bandCount={bandCount} />
              <div style={{ marginTop: 12, fontSize: 13, color: "#C8CADF", textAlign: "center" }}>
                {encoded.bands.map((b) => COLORS[b].name).join(", ")}
              </div>
            </div>
          )}
        </>
      )}

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 18, textAlign: "center" }}>
        Standard IEC 60062 color codes for through-hole resistors. SMD resistors use a different numeric coding.
      </p>
    </ToolsShell>
  );
}

function ResistorPreview({ bands, bandCount }) {
  // Visual resistor: tan body, lead wires, colored bands.
  const W = 320, H = 90;
  const bodyX = 40, bodyW = 240, bodyY = 25, bodyH = 40;
  const bandW = 12;
  const bandGap = (bodyW - bands.length * bandW) / (bands.length + 1) * 0.7;
  const bandStartX = bodyX + (bodyW - (bands.length * bandW + (bands.length - 1) * bandGap)) / 2;
  return (
    <div style={{ background: "#13141F", borderRadius: 12, padding: "16px 0", display: "flex", justifyContent: "center" }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
        {/* Lead wires */}
        <line x1="0" y1={H / 2} x2={bodyX} y2={H / 2} stroke="#999" strokeWidth="2" />
        <line x1={bodyX + bodyW} y1={H / 2} x2={W} y2={H / 2} stroke="#999" strokeWidth="2" />
        {/* Body */}
        <rect x={bodyX} y={bodyY} width={bodyW} height={bodyH} rx="6" fill="#D4A574" stroke="#8B6B40" strokeWidth="1.2" />
        {/* Bands */}
        {bands.map((c, i) => (
          <rect
            key={i}
            x={bandStartX + i * (bandW + bandGap)}
            y={bodyY}
            width={bandW}
            height={bodyH}
            fill={COLORS[c].hex}
          />
        ))}
      </svg>
    </div>
  );
}
